// ui/src/components/shared/ProvisionalBadge.jsx

/**
 * @file ProvisionalBadge.jsx
 * @description Provisional figure badge with relative freshness timestamp.
 *   Exports `relativeTime` and `fullDateTime` formatters consumed by
 *   DataFreshnessIndicator and other shared components.
 *   No API calls — prop-driven display only.
 * @module shared
 * @author data-fit LLC · DecisionFlow · S775
 * @see api_spec_fileB_v4_final.md — isProvisional flag (ep 45–52)
 */

import React from 'react';

/**
 * Shared freshness formatter.
 *
 * @param {string|null|undefined} isoString
 * @returns {string}
 */
export function relativeTime(isoString) {
  if (!isoString) return 'Freshness unknown';

  const timestamp = new Date(isoString);
  if (Number.isNaN(timestamp.getTime())) return 'Freshness unknown';

  const diff = Math.max(0, Date.now() - timestamp.getTime());
  const mins = Math.floor(diff / 60000);

  if (mins < 1) return 'Just updated';
  if (mins < 60) return `Updated ${mins} min ago`;

  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `Updated ${hrs}h ago`;

  return `Updated ${timestamp.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  })}`;
}

/**
 * @param {string|null|undefined} isoString
 * @returns {string}
 */
export function fullDateTime(isoString) {
  if (!isoString) return 'unknown time';

  const timestamp = new Date(isoString);
  if (Number.isNaN(timestamp.getTime())) return 'unknown time';

  return timestamp.toLocaleString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}

/**
 * @param {{
 *   freshnessTimestamp: string,
 *   label?: string,
 *   completenessState?: 'full'|'partial'|null,
 *   size?: 'sm'|'md',
 *   className?: string
 * }} props
 */
export default function ProvisionalBadge({
  freshnessTimestamp,
  label = 'Provisional',
  completenessState = null,
  size = 'md',
  className = '',
}) {
  const freshness = relativeTime(freshnessTimestamp);
  const fullTimestamp = fullDateTime(freshnessTimestamp);

  const displayLabel =
    completenessState === 'partial'
      ? `${label} · partial coverage`
      : label;

  const fontSize = size === 'sm' ? '10px' : '11px';

  return (
    <span
      className={`inline-flex flex-wrap items-center gap-x-1.5 rounded-[999px] border px-[10px] py-[2px] font-medium leading-4 ${className}`}
      style={{
        fontSize,
        color: 'rgb(var(--color-warning))',
        backgroundColor: 'rgb(var(--color-warning) / 0.12)',
        borderColor: 'rgb(var(--color-warning) / 0.35)',
      }}
      aria-label={`Provisional figure, last updated ${fullTimestamp}${
        completenessState === 'partial' ? ', partial coverage' : ''
      }`}
    >
      <span>{displayLabel}</span>

      <span aria-hidden="true">·</span>

      <span
        className={
          freshness === 'Freshness unknown'
            ? 'text-[rgb(var(--color-text-muted))]'
            : ''
        }
      >
        {freshness}
      </span>
    </span>
  );
}
